import {createContext, Dispatch, FC, ReactNode, useReducer, useContext} from "react";

export type ManifestDetailsState = {
  manifestDriverId: number | undefined;
  driverId: number | undefined;
};

const InitialManifestDetailsState: ManifestDetailsState = {
  manifestDriverId: undefined,
  driverId: undefined
};

export type ManifestDetailsActions =
  | {
      type: "SET_MANIFEST_DETAILS";
      payload: {
        manifestDriverId: number | undefined;
        driverId: number | undefined;
      };
    }
  | {
      type: "CLEAR_MANIFEST_DETAILS";
    };

export const ManifestDetailsReducer = (
  state: ManifestDetailsState,
  action: ManifestDetailsActions
): ManifestDetailsState => {
  switch (action.type) {
    case "SET_MANIFEST_DETAILS":
      return {
        ...state,
        manifestDriverId: action.payload.manifestDriverId,
        driverId: action.payload.driverId
      };
    case "CLEAR_MANIFEST_DETAILS":
      return {
        ...state,
        manifestDriverId: undefined,
        driverId: undefined
      };
    default:
      return state;
  }
};

export const ManifestDetailsStateContext = createContext<ManifestDetailsState | undefined>(undefined);
export const ManifestDetailsDispatchContext = createContext<Dispatch<ManifestDetailsActions> | undefined>(undefined);

export const ManifestDetailsProvider: FC<{children: ReactNode}> = ({children}) => {
  const [manifestDetailsState, manifestDetailsStateDispatch] = useReducer(
    ManifestDetailsReducer,
    InitialManifestDetailsState
  );

  return (
    <ManifestDetailsStateContext.Provider value={manifestDetailsState}>
      <ManifestDetailsDispatchContext.Provider value={manifestDetailsStateDispatch}>
        {children}
      </ManifestDetailsDispatchContext.Provider>
    </ManifestDetailsStateContext.Provider>
  );
};

export const useManifestDetailsState = () => {
  const context = useContext(ManifestDetailsStateContext);
  if (!context) {
    throw new Error("useManifestDetailsState must be used within a ManifestDetailsProvider");
  }
  return context;
};

export const useManifestDetailsDispatch = () => {
  const context = useContext(ManifestDetailsDispatchContext);
  if (!context) {
    throw new Error("useManifestDetailsDispatch must be used within a ManifestDetailsProvider");
  }
  return context;
};
